import { Toast } from '@florencelabs/toast';
import { useEffect, useMemo, ReactElement } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { AppointmentSummary } from '@provider-directory/components/appointment-summary/appointment-summary.component';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';

import { SplitScreen } from '@provider-directory/components/split-screen/split-screen.component';
import { useAppointment } from '@provider-directory/contexts/AppointmentContext';
import { useProvider } from '@provider-directory/contexts/ProviderContext';
import { EMPTY_INSURANCE_FIELDS } from '@provider-directory/models/appointment.model';
import {
  getProviderAvailableInsurances,
  InsuranceResolutionContext,
} from '@provider-directory/models/insurance.model';
import { ScheduleForm, FormConfig, FormBlockType } from '../forms';
import { SceneFormWithNavigation } from '../scene-form/scene-form.component';

export const insuranceInfoValues = z.object({
  usingInsurance: z.string().optional(),
  insuranceCarrier: z.string().optional(),
  memberID: z.string().optional(),
  groupNumber: z.string().optional(),
});
export type InsuranceInfoValues = z.infer<typeof insuranceInfoValues>;

export default function Insurance({
  onPrev: onPrevFromProps,
  onNext,
}: {
  onPrev: () => void;
  onNext: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { provider } = useProvider();
  const { appointment, updateAppointment } = useAppointment();
  const analytics = useAnalytics();
  const { key: orgKey, runtimeConfig } = useOrgContext();
  const methods = useForm<InsuranceInfoValues>({
    mode: 'onTouched',
  });

  const { watch } = methods;
  const usingInsurance = watch('usingInsurance');

  const yesLabel = useMemo(() => t('yes'), [t]);
  const noLabel = useMemo(() => t('no'), [t]);
  const hospitalAssociations = provider?.hospitalAssociations ?? [];

  const resolutionContext: InsuranceResolutionContext = {
    hospitalAssociationMapping: runtimeConfig.hospitalAssociationMapping,
    insuranceResolutionRule: runtimeConfig.insuranceResolutionRule,
    insurance: runtimeConfig.insurance,
    fallbackKey: orgKey,
  };

  const acceptedInsurances = getProviderAvailableInsurances(
    hospitalAssociations,
    resolutionContext,
  );

  const insuranceOptions = acceptedInsurances.map((insurance) => ({
    label: insurance,
    value: insurance,
  }));

  const InsuranceInfoConfig: FormConfig<InsuranceInfoValues> = useMemo(() => {
    const form: FormConfig<InsuranceInfoValues> = [
      {
        blockId: 'usingInsurance',
        type: FormBlockType.SECTION,
        children: [
          {
            type: FormBlockType.RADIO,
            name: 'usingInsurance',
            label: 'schedule.insuranceInfo.usingInsurance.title',
            options: [
              { value: yesLabel, label: yesLabel },
              { value: noLabel, label: noLabel },
            ],
            validation: {
              required: 'selectAnOption',
            },
            onOptionSelected: (option) => {
              if (option.label !== noLabel) return;

              updateAppointment({
                ...EMPTY_INSURANCE_FIELDS,
                usingInsurance: noLabel,
              });
            },
          },
        ],
      },
      {
        blockId: 'insuranceInfo',
        type: FormBlockType.SECTION,
        title: 'schedule.insuranceInfo.insuranceProvider.title',
        isHiddenValidation: (formStatus: InsuranceInfoValues) => {
          if (formStatus.usingInsurance === undefined) {
            return true;
          }
          return formStatus.usingInsurance === noLabel;
        },
        children: [
          {
            name: 'insuranceCarrier',
            label: 'schedule.insuranceInfo.insuranceProvider.carrier',
            type: FormBlockType.SELECT,
            options: insuranceOptions,
            validation: {
              required:
                'schedule.insuranceInfo.insuranceProvider.requiredInsuranceCarrier',
            },
          },
          {
            type: FormBlockType.ROW,
            blockId: 'insurance-member-group',
            children: [
              {
                name: 'memberID',
                label: 'schedule.insuranceInfo.insuranceProvider.memberID',
                type: FormBlockType.TEXT,
                validation: {
                  required:
                    'schedule.insuranceInfo.insuranceProvider.requiredMemberId',
                },
              },
              {
                name: 'groupNumber',
                label: 'schedule.insuranceInfo.insuranceProvider.groupNumber',
                type: FormBlockType.TEXT,
              },
            ],
          },
        ],
      },
    ];
    return form;
  }, [yesLabel, noLabel, insuranceOptions, updateAppointment]);

  const onSubmit: SubmitHandler<{}> = (data) => {
    analytics.completedFormEvent({
      formStep: 'InsuranceInfo',
      linkContextPage: 'SchedulingInsurance',
      provider: provider ?? undefined,
    });
    updateAppointment(data);
    onNext();
  };

  const onPrev = () => {
    updateAppointment(EMPTY_INSURANCE_FIELDS);
    onPrevFromProps();
  };

  useEffect(() => {
    const formFields: InsuranceInfoValues = {
      usingInsurance: appointment.usingInsurance || undefined,
      insuranceCarrier: appointment.insuranceCarrier || undefined,
      memberID: appointment.memberID || undefined,
      groupNumber: appointment.groupNumber || undefined,
    };
    Object.entries(formFields).forEach(([key, value]) => {
      methods.setValue(key as keyof InsuranceInfoValues, value);
    });
  }, [appointment, methods]);

  return (
    <SplitScreen
      additionalClassnames={{
        main: 'bg-white',
        right: {
          screen: 'desktop:bg-primary-50',
        },
      }}
    >
      <SceneFormWithNavigation
        onSubmit={methods.handleSubmit(onSubmit)}
        onPrev={onPrev}
        formId={'insurance-form'}
      >
        <h1
          className={
            'font-serif text-display-large desktop:text-display-xlarge'
          }
        >
          {t('schedule.scheduleAnAppointment')}
        </h1>
        <ScheduleForm<InsuranceInfoValues>
          className={'flex-1'}
          blocks={InsuranceInfoConfig}
          control={methods.control}
          watch={watch}
        />
        {usingInsurance === t('yes') && (
          <Toast
            variant={'warning'}
            layout={'multiLine'}
            isDismissible={false}
            description={t('details.insuranceEligibility')}
            cornerRadius={'medium'}
          />
        )}
      </SceneFormWithNavigation>
      <AppointmentSummary />
    </SplitScreen>
  );
}
